import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import {
  BASE_BILLING_SETTINGS_PATH,
  BILLING_SETTINGS_PATH,
  CANCELLED_AGREEMENT_SETTINGS_PATH,
  FAILED_AGREEMENT_SETTINGS_PATH,
  LOCAL_STORAGE_FAILED_PAYMENT_KEY,
  MOBILE_SEARCH,
  PAGE_SPINNER_MARGIN_FROM_TOP,
  PAID_AGREEMENT_SETTINGS_PATH,
  PROCESSED_AGREEMENT_SETTINGS_PATH,
} from "common/constants";
import failureImage from "images/failure.png";
import successImage from "images/success.png";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import React, { useEffect } from "react";
import styled, { css, useTheme } from "styled-components";
import { NEW_LOOK_PAGE_GUTTER, usePageIntro } from "common/NewLook";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import Button from "common/Buttons/Button";
import useQuery from "utils/hooks/useQuery";
import { generatePath, useHistory, useRouteMatch } from "react-router-dom";
import useMedia from "utils/hooks/useMedia";
import PaymentInfo from "features/settings/Subscription/PaymentInfo";
import Flex from "common/Boxes/Flex";
import LinkOutlined from "@ant-design/icons/LinkOutlined";
import { skipToken } from "@reduxjs/toolkit/query";
import { typography } from "@shared/common/Themes/typography";
import { LanguageNameSpaces, PaymentType } from "@shared/types";
import { BillingTab } from "features/settings/common/types";
import { sharedLib } from "@shared/services/shared.service";
import { HEADER_HEIGHT } from "common/Home/General/constants.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { CircleAlert, CircleCheck, CircleX, Clock3 } from "lucide-react";
import { hexToRGB } from "@shared/utils/misc";

const PaymentStatus = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);

  const isProcessedAgreement = Boolean(
    useRouteMatch(PROCESSED_AGREEMENT_SETTINGS_PATH)
  );
  const isFailedAgreement = Boolean(
    useRouteMatch(FAILED_AGREEMENT_SETTINGS_PATH)
  );
  const isCancelAgreement = Boolean(
    useRouteMatch(CANCELLED_AGREEMENT_SETTINGS_PATH)
  );
  const isPaidAgreement = Boolean(useRouteMatch(PAID_AGREEMENT_SETTINGS_PATH));
  const query = useQuery();
  const pt = query.get("pt");
  const {
    data: billingAgreementDetails,
    isLoading: isLoadingBillingAgreement,
  } = sharedLib.sharedApi.settings.useGetBillingAgreementDetailsQuery(
    pt
      ? {
          payment_token: pt,
        }
      : skipToken
  );
  const history = useHistory();
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  // A focused result screen: no page intro above it in the new look.
  usePageIntro({ hidden: appTheme.isNewLook }, [appTheme.isNewLook]);

  useEffect(() => {
    if (isFailedAgreement) {
      localStorage?.setItem(LOCAL_STORAGE_FAILED_PAYMENT_KEY, "1");
    }
  }, [isFailedAgreement]);

  const onFinishClick = () => {
    const path = billingAgreementDetails?.sub_group_name
      ? generatePath(BILLING_SETTINGS_PATH, {
          tab: BillingTab.GROUPS,
        })
      : BASE_BILLING_SETTINGS_PATH;
    history.push(path);
  };

  let IconStyle = { margin: isMobile ? 0 : "16 0 32 0" };
  const isUpgrade =
    billingAgreementDetails?.payment_type === PaymentType.UPGRADE;
  const isSlots = billingAgreementDetails?.payment_type === PaymentType.SLOTS;
  const isProratedSlots =
    billingAgreementDetails?.payment_type === PaymentType.PRORATED_SLOTS;

  const getIcon = () => {
    if (isProcessedAgreement || isPaidAgreement)
      return <img src={successImage} style={IconStyle} alt="paymentSuccess" />;
    return <img src={failureImage} style={IconStyle} alt="paymentFailed" />;
  };

  const getTitle = () => {
    if (isProcessedAgreement) {
      if (isUpgrade) return t("payment_status.processing_upgrade_payment");
      if (isSlots) return t("payment_status.processing_slots_payment");
      if (isProratedSlots)
        return t("payment_status.processing_prorated_slots_payment");
      return t("payment_status.processing_your_payment");
    } else if (isPaidAgreement) {
      if (isUpgrade) return t("payment_status.upgrade_payment_processed");
      if (isSlots) return t("payment_status.slots_payment_processed");
      if (isProratedSlots)
        return t("payment_status.prorated_slots_payment_processed");
      return t("payment_status.your_payment_processed");
    } else if (isFailedAgreement) return t("payment_status.paypal_failed");
    else return t("payment_status.payment_not_completed");
  };

  const getDescription = () => {
    if (isProcessedAgreement)
      return t("payment_status.notified_payment_processed");
    else if (isFailedAgreement)
      return t("payment_status.check_paypal_payment_option");
    else if (isCancelAgreement)
      return (
        billingAgreementDetails?.payer_view_url && (
          <div>
            {t("payment_status.complete_payment")}
            <Flex $justifyContent="center" style={{ marginTop: 20 }}>
              <Button
                type="primary"
                href={billingAgreementDetails?.payer_view_url}
                style={{ marginRight: 8 }}
                icon={<LinkOutlined />}
              >
                {t("payment_status.pay_now")}
              </Button>
              <Button href={BASE_BILLING_SETTINGS_PATH}>
                {t("payment_status.take_me_back")}
              </Button>
            </Flex>
          </div>
        )
      );
  };

  if (appTheme.isNewLook) {
    const status = isPaidAgreement
      ? { icon: CircleCheck, color: appTheme.colors.green }
      : isProcessedAgreement
      ? { icon: Clock3, color: appTheme.colors.primaryBlue }
      : isFailedAgreement
      ? { icon: CircleX, color: appTheme.colors.red }
      : { icon: CircleAlert, color: appTheme.colors.gold };
    const StatusIcon = status.icon;
    const description = getDescription();
    return (
      <Wrapper>
        <LoadingSpinnerOverlay
          isLoading={isLoadingBillingAgreement}
          spinnerSize="large"
          spinnerMarginTop={120}
          backgroundColor={appTheme.colors.cardBgColor}
          hideContentWhileLoading={isLoadingBillingAgreement}
        >
          <NewLookStatus>
            <NewLookStatusIcon
              style={{
                color: status.color,
                background: hexToRGB(status.color, 0.1),
              }}
            >
              <StatusIcon size={24} strokeWidth={1.75} />
            </NewLookStatusIcon>
            <StatusTitle level={1}>{getTitle()}</StatusTitle>
            {(isProcessedAgreement || isPaidAgreement) &&
              billingAgreementDetails && (
                <PaymentInfo
                  billingAgreementDetails={billingAgreementDetails}
                />
              )}
            {description && (
              <NewLookDescription>{description}</NewLookDescription>
            )}
            {!isCancelAgreement && (
              <FinishButton type="primary" onClick={onFinishClick}>
                OK
              </FinishButton>
            )}
          </NewLookStatus>
        </LoadingSpinnerOverlay>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <LoadingSpinnerOverlay
        isLoading={isLoadingBillingAgreement}
        spinnerSize="large"
        spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
        backgroundColor={appTheme.colors.cardBgColor}
        hideContentWhileLoading={isLoadingBillingAgreement}
      >
        {getIcon()}
        <TextsWrapper>
          <StatusTitle level={1}>{getTitle()}</StatusTitle>
          {(isProcessedAgreement || isPaidAgreement) &&
            billingAgreementDetails && (
              <PaymentInfo billingAgreementDetails={billingAgreementDetails} />
            )}
          <Text
            as="div"
            style={{ marginTop: 16 }}
            $size={typography.fontSizes.XL}
            type="secondary"
          >
            {getDescription()}
          </Text>
        </TextsWrapper>
        {!isCancelAgreement && (
          <FinishButton type="primary" onClick={onFinishClick}>
            OK
          </FinishButton>
        )}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default PaymentStatus;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  padding: 40px;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  min-height: calc(100vh - ${HEADER_HEIGHT}px);

  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 20px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-height: auto;
      margin: 32px auto;
      width: calc(100% - ${NEW_LOOK_PAGE_GUTTER * 2}px);
      max-width: 520px;
      padding: 32px;
      border: ${p.theme.borders.panel};
      border-radius: ${p.theme.radii.card}px;
      box-shadow: ${p.theme.shadows.panel};
      @media (max-width: ${MOBILE_SEARCH}) {
        margin: 16px;
        width: calc(100% - 32px);
        padding: 24px 20px;
      }
    `}
`;
const TextsWrapper = styled.div`
  max-width: 600px;
  text-align: center;
`;
const StatusTitle = styled(Title)`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        margin: 20px 0 0;
        ${newLookType.pageTitle};
        color: ${getNewLookTokens(p.theme).ink};
      }
    `}
`;
const FinishButton = styled(Button)`
  margin-top: 20px;
  width: 200px;
  height: 40px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      width: 160px;
      margin-top: 32px;
      @media (max-width: ${MOBILE_SEARCH}) {
        margin-top: 24px;
      }
    `}
`;

const NewLookStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const NewLookStatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
`;
const NewLookDescription = styled.div`
  margin-top: 8px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
